import { useCallback, useEffect, useRef, useState } from 'react';
import { useI18n, type CopyKey } from './i18n';
import {
  isError,
  listSources,
  presignUpload,
  readExtracted,
  type ExtractedContent,
  type Source,
} from './api';

/**
 * Adding material, and seeing what was read out of it.
 *
 * The second half is the point. A photograph of a page is only worth uploading if
 * you can tell whether it was read correctly — an extraction nobody checks is a
 * confidently wrong curriculum waiting to happen.
 */

/** What the file picker will offer, matching what the API will sign. */
const ACCEPT = 'image/jpeg,image/png,image/heic,image/webp,application/pdf';

/** Polling stops here. Extraction is seconds for an image, longer for a PDF. */
const POLL_ATTEMPTS = 40;
const POLL_INTERVAL_MS = 3000;

export default function Sources({ objectiveId }: { objectiveId: string }) {
  const { t } = useI18n();
  const [sources, setSources] = useState<Source[] | undefined>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [open, setOpen] = useState<Record<string, ExtractedContent | 'loading' | undefined>>({});
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    const result = await listSources(objectiveId);
    if (!isError(result)) setSources(result.sources);
    else setSources([]);
    return isError(result) ? [] : result.sources;
  }, [objectiveId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Waits for extraction to finish, then stops.
   *
   * Polling rather than a push, because the alternative is a websocket for one
   * event a learner is already watching for. It gives up rather than spinning
   * forever: a source stuck at `pending` past the timeout is a real failure, and
   * saying so beats a spinner that never resolves.
   */
  async function waitForExtraction(sourceId: string) {
    for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      const current = await load();
      const source = current.find((s) => s.sourceId === sourceId);
      if (source && source.status !== 'pending') return;
    }
  }

  async function upload(file: File) {
    setBusy(true);
    setProblem(undefined);

    const signed = await presignUpload(objectiveId, file.type, file.name);
    if (isError(signed)) {
      setBusy(false);
      setProblem(
        signed.error === 'unsupported_type' ? t('src.error.type') : t('src.error.generic'),
      );
      return;
    }

    try {
      // Straight to S3. The content type must match what was signed, or the
      // signature does not cover the request and S3 refuses it.
      const response = await fetch(signed.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!response.ok) throw new Error(`S3 returned ${response.status}`);
    } catch {
      setBusy(false);
      setProblem(t('src.error.upload'));
      return;
    }

    await load();
    await waitForExtraction(signed.sourceId);
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function toggle(sourceId: string) {
    if (open[sourceId]) {
      setOpen((prior) => ({ ...prior, [sourceId]: undefined }));
      return;
    }
    setOpen((prior) => ({ ...prior, [sourceId]: 'loading' }));
    const result = await readExtracted(objectiveId, sourceId);
    setOpen((prior) => ({
      ...prior,
      [sourceId]: isError(result) ? undefined : result.content,
    }));
  }

  return (
    <section className="panel">
      <h2>{t('src.title')}</h2>
      <p className="muted">{t('src.hint')}</p>

      <div className="actions">
        <label className="button primary file-button">
          {busy ? t('src.working') : t('src.choose')}
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            disabled={busy}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void upload(file);
            }}
          />
        </label>
      </div>

      {problem && (
        <p className="problem" role="alert">
          {problem}
        </p>
      )}

      {sources?.length === 0 && <p className="muted">{t('src.empty')}</p>}

      <ul className="sources">
        {sources?.map((source) => (
          <li key={source.sourceId}>
            <p className="meta">
              <span className={`pill pill-${source.status}`}>
                {t(`src.status.${source.status}` as CopyKey)}
              </span>{' '}
              {source.title ?? source.sourceId}
            </p>

            {source.status === 'failed' && (
              // Named, not generalised: somebody who photographed eight pages
              // needs to know which one was unreadable.
              <p className="muted">{source.detail ?? t('src.error.generic')}</p>
            )}

            {source.status === 'extracted' && (
              <>
                <button type="button" className="linky" onClick={() => void toggle(source.sourceId)}>
                  {open[source.sourceId] ? t('src.hide') : t('src.show')}
                </button>
                {open[source.sourceId] === 'loading' && <p className="muted">{t('src.loading')}</p>}
                {open[source.sourceId] && open[source.sourceId] !== 'loading' && (
                  <>
                    <p className="muted small">
                      {(open[source.sourceId] as ExtractedContent).method} ·{' '}
                      {(open[source.sourceId] as ExtractedContent).body.length}{' '}
                      {t('src.characters')}
                    </p>
                    <pre className="extracted">
                      {(open[source.sourceId] as ExtractedContent).body}
                      {(open[source.sourceId] as ExtractedContent).truncated
                        ? `\n\n… ${t('src.truncated')}`
                        : ''}
                    </pre>
                  </>
                )}
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
